<template>
  <div class="group -flex -full site-notification" :class="typeClassList">
    <div class="notification-flair">
      <div v-if="notification.fromUser" class="notification-type -member-message">
        <img
          v-if="notification.fromAvatarUrl"
          class="author-image"
          :src="notification.fromAvatarUrl"
          alt=""
        />
        <span v-else class="author-initial">
          {{ notification.fromUser.displayName.charAt(0).toUpperCase() }}
        </span>
      </div>
      <div
        v-else-if="notification.notifiableType === 'admin_notification'"
        :class="`notification-type ${typeClassName} ${levelClassName}`"
      >
        <img class="author-image" :src="adminNotificationImage" alt="" />
      </div>
      <div v-else :class="`notification-type ${typeClassName} ${levelClassName}`">
        <img v-if="achievementImage" class="author-image" :src="achievementImageUrl" alt="" />
        <i v-else class="material-symbols-outlined" aria-hidden="true">{{ notificationIcon }}</i>
      </div>
    </div>

    <div class="notification-content">
      <div v-if="error" class="message">
        <callout type="error">
          <div>{{ error }}</div>
        </callout>
      </div>

      <div class="timestamp">{{ notification.timeAgo }}</div>
      <h1
        class="title"
        role="button"
        tabindex="0"
        :aria-expanded="isExpanded"
        :aria-controls="`notification-${notification.id}-message`"
        @click="toggleExpanded"
        @keyup.enter="toggleExpanded"
      >
        {{ notification.title }}
      </h1>
      <!-- eslint-disable vue/no-v-html -->
      <div :id="`notification-${notification.id}-message`" :aria-hidden="!isExpanded">
        <div class="message" v-html="notification.body" />
        <div
          v-if="
            notification.notifiableType === 'admin_notification' &&
            notification.notifiable &&
            notification.notifiable.imageUrl
          "
          class="image"
        >
          <img :src="notification.notifiable.imageUrl" loading="lazy" alt="" />
        </div>
        <div v-if="notification.urlCta" class="cta -flex -center">
          <a class="btn -dark" :href="notification.url">{{ notification.urlTitle }}</a>
        </div>
      </div>

      <div class="actions">
        <div class="action">
          <a
            :href="notification.url"
            aria-label="View"
            class="btn -material -no-border -ico-txt"
            @click="markRead"
          >
            <i class="material-symbols-outlined btn-ico" aria-hidden="true">preview</i>
            <span class="btn-txt -reduce">View</span>
          </a>
        </div>
        <div class="action">
          <icon-button
            icon="mark_email_unread"
            classes="-no-border -ico-txt"
            label="Mark Unread"
            @click-action="markUnreadAndCollapse"
          />
        </div>
        <div class="action">
          <icon-button
            icon="delete"
            classes="-no-border -ico-txt"
            label="Delete"
            :submitting="deleteLoading"
            submitting-label="Deleting..."
            @click-action="deleteNotification"
          />
        </div>
      </div>
    </div>
    <div class="notification-view">
      <icon-button
        :icon="expandedIcon"
        classes="expand-collapse -no-border -ico-only"
        :label="isExpanded ? 'Expanded' : 'Collapsed'"
        aria-live="polite"
        :aria-expanded="isExpanded ? 'true' : 'false'"
        :aria-controls="`notification-${notification.id}-message`"
        @click-action="toggleExpanded"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import { mapActions, mapState } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import IconButton from '@/components/buttons/icon-button.vue'
import { fbApi, FbApiError } from '@/utils/http-api'
import type { UserNotification } from '@/types/model-types'
import Callout from '@/components/notifications/callout.vue'

export default defineComponent({
  components: {
    Callout,
    IconButton,
  },

  props: {
    notification: {
      type: Object as PropType<UserNotification>,
      required: true,
    },

    previewMode: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['deleted', 'updating', 'updated'],

  data() {
    return {
      isExpanded: false,
      error: '',
      markReadLoading: false,
      deleteLoading: false,
    }
  },

  computed: {
    ...mapState(useUserAuthStore, ['user', 'cdn']),

    typeClassList() {
      const expanded = this.isExpanded ? ' -expanded' : ''
      const read = this.notification.isRead ? ' -read' : ''

      return `${this.typeClassName}${expanded}${read}`
    },

    typeClassName() {
      switch (this.notification.notifiableType) {
        case 'achievement_level':
          return '-achievement'
        case 'comment':
          return '-member-message'
        case 'coupon':
          return '-gift'
        case 'admin_notification':
          return '-fb-admin'
        default:
          return '-fb-message'
      }
    },

    levelClassName() {
      if (this.notification.notifiable && this.notification.notifiable.levelStyle) {
        return this.notification.notifiable.levelStyle
      }

      return ''
    },

    expandedIcon() {
      return this.isExpanded ? 'expand_less' : 'expand_more'
    },

    imageUrlPrefix() {
      // get siteassets url from page load, allows CDN url to be added
      return this.cdn.siteAssetsPrefix || '/siteassets'
    },

    adminNotificationImage() {
      return `${this.cdn.prefix}/assets/img/fb-avatar.svg`
    },

    //use es6 template strings
    achievementImageUrl() {
      return `${this.imageUrlPrefix}/achievements/${this.notification.notifiable.achievement.image}`
    },

    achievementImage() {
      return (
        this.notification.notifiable &&
        this.notification.notifiable.achievement &&
        this.notification.notifiable.achievement.image
      )
    },

    notificationIcon() {
      const notificationType = this.notification.notifiableType

      switch (notificationType) {
        case 'achievement_level':
          if (this.notification && this.notification.notifiable) {
            return this.notification.notifiable.achievement.imageIcon
          }
          return 'mail'
        case 'comment':
          return ''
        case 'coupon':
          return 'card_giftcard'
      }
      return 'mail'
    },
  },

  methods: {
    ...mapActions(useUserAuthStore, ['updateUser']),

    toggleExpanded() {
      if (!this.isExpanded && !this.notification.isRead) {
        this.markRead()
      }
      this.isExpanded = !this.isExpanded
    },

    // When called will collapse the notification and calls API to mark unread
    markUnreadAndCollapse() {
      this.setIsRead(0)
      this.isExpanded = false
    },

    // Called on expand (toggleExpanded()) and makes API call to mark as read
    markRead() {
      // note: this call doesn't wait for response if done on a link
      if (!this.notification.isRead) {
        this.setIsRead(1)
      }
    },

    // deleteAction
    async deleteNotification() {
      // short circuit if in fbadmin preview
      if (this.previewMode) {
        return
      }

      // no double submit
      if (this.deleteLoading) {
        return
      }
      this.deleteLoading = true
      this.error = ''

      // api call
      const url = `/notifications/${this.notification.id}/delete`

      // note: on error does not try to get back to previous state
      try {
        const res = await fbApi.delete(url)

        if (!res || !res.body) {
          throw new Error(`Unable to update notification, please try again`)
        }

        this.$emit('deleted', this.notification)
      } catch (err) {
        // treat 404 as success
        if (err && err instanceof FbApiError && err.status === 404) {
          this.$emit('deleted', this.notification)
        } else {
          this.error = `Unable to update notification, please try again`
        }
      } finally {
        this.deleteLoading = false
      }
    },

    // mark read (true) or unread (false)
    async setIsRead(newState: number) {
      // short circuit if in fbadmin preview
      if (this.previewMode) {
        return
      }

      if (this.markReadLoading) {
        return
      }

      const currUnreadCount = this.user.notificationUnreadCount

      this.markReadLoading = true
      // Emitting to the parent component first to instantly update the notification
      // On error, emit again with opposite state to reset
      this.$emit('updating', {
        id: this.notification.id,
        isRead: newState,
      })

      // switch notification's is_read state
      const readUrl = this.notification.isRead ? 'read' : 'unread'
      const url = `/notifications/${this.notification.id}/${readUrl}`

      try {
        const res = await fbApi.put(url)

        if (!res || !res.body) {
          throw new Error(`Unable to update notification, please try again`)
        }

        this.error = ''
        this.markReadLoading = false
        this.updateUser({ notificationUnreadCount: res.body.data.unreadCount })

        this.$emit('updated', {
          id: this.notification.id,
          isRead: newState,
        })
      } catch (err) {
        this.error = `Unable to update notification, please try again`
        this.markReadLoading = false
        this.updateUser({ notificationUnreadCount: currUnreadCount })

        this.$emit('updated', {
          id: this.notification.id,
          isRead: !newState,
        })
      }
    },
  },
})
</script>
